import React from "react";
import { Card, CardBody, Row, Col, FormGroup, Label, Button } from "reactstrap";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  USER_DETAIL_QUERY,
  USER_EDIT_MUTATION,
  CAREGIVER_INFO_EDIT_MUTATION,
  USER_LIST_QUERY,
} from "../../config/Queries";

function formatDateForInputFromMs(ms) {
  if (!Number.isFinite(ms)) return "";
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseDateInputToLocalMs(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split("-").map((v) => parseInt(v, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const ms = new Date(y, m - 1, d, 0, 0, 0, 0).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function CaregiverEdit({ match }) {
  const history = useHistory();
  const userCode = parseInt(match.params.id);
  const isValidUserCode = Number.isFinite(userCode);

  const { data, loading } = useQuery(USER_DETAIL_QUERY, {
    skip: !isValidUserCode,
    variables: { code: userCode },
  });

  const { register, handleSubmit } = useForm();

  const [editAccount, { loading: savingAccount }] = useMutation(USER_EDIT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: () => [
      { query: USER_DETAIL_QUERY, variables: { code: userCode } },
      // CaregiverList에서 사용하는 variables와 동일하게 맞춰야 즉시 반영됩니다.
      { query: USER_LIST_QUERY, variables: { type: "간병인" } },
    ],
  });

  const [editCaregiverInfo, { loading: savingInfo }] = useMutation(
    CAREGIVER_INFO_EDIT_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: () => [{ query: USER_DETAIL_QUERY, variables: { code: userCode } }],
    }
  );

  const saving = savingAccount || savingInfo;

  const onSubmit = async (form) => {
    try {
      if (!isValidUserCode) {
        toast.error("잘못된 회원 코드입니다. 목록에서 다시 선택해주세요.", {
          autoClose: 3000,
          position: toast.POSITION.TOP_RIGHT,
        });
        history.push("/admin/caregivers");
        return;
      }

      const profile = data?.viewProfile;
      if (!profile) {
        toast.error("회원 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.", {
          autoClose: 3000,
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }
      const info = profile?.caregiverInfo || {};

      const nextUserName = (form.userName || "").trim();
      const nextPhone = (form.phone || "").trim();
      const nextPassword = (form.password || "").trim();
      const nextJoinDateMs = parseDateInputToLocalMs(form.createdAtDate);

      const accountVariables = { userCode };
      if (profile?.userName !== nextUserName) accountVariables.userName = nextUserName;
      if (profile?.phone !== nextPhone) accountVariables.phone = nextPhone;
      if (nextPassword) accountVariables.password = nextPassword;
      if (
        Number.isFinite(nextJoinDateMs) &&
        parseInt(profile?.createdAt, 10) !== nextJoinDateMs
      ) {
        accountVariables.createdAt = String(nextJoinDateMs);
      }

      if (Object.keys(accountVariables).length > 1) {
        const accountRes = await editAccount({ variables: accountVariables });
        if (!accountRes?.data?.editAccount?.ok) {
          throw new Error(
            accountRes?.data?.editAccount?.error || "계정 정보 수정에 실패했습니다."
          );
        }
      }

      const nextAddress = (form.address || "").trim();
      const nextAddressDetail = (form.addressDetail || "").trim();
      const nextResidentNumber = (form.residentNumber || "").trim();
      const nextSmoke = (form.smoke || "").trim();
      const nextDrink = (form.drink || "").trim();
      const nextCareer = (form.career || "").trim();
      const nextMealCare = (form.mealCare || "").trim();
      const nextUrineCare = (form.urineCare || "").trim();
      const nextSuctionCare = (form.suctionCare || "").trim();
      const nextMoveCare = (form.moveCare || "").trim();
      const nextBedCare = (form.bedCare || "").trim();
      const nextIntroduce = (form.introduce || "").trim();

      const infoVariables = { userCode };
      if ((info?.address || "") !== nextAddress) infoVariables.address = nextAddress;
      if ((info?.addressDetail || "") !== nextAddressDetail)
        infoVariables.addressDetail = nextAddressDetail;
      if ((info?.residentNumber || "") !== nextResidentNumber)
        infoVariables.residentNumber = nextResidentNumber;
      if ((info?.smoke || "") !== nextSmoke) infoVariables.smoke = nextSmoke;
      if ((info?.drink || "") !== nextDrink) infoVariables.drink = nextDrink;
      if ((info?.career || "") !== nextCareer) infoVariables.career = nextCareer;
      if ((info?.mealCare || "") !== nextMealCare) infoVariables.mealCare = nextMealCare;
      if ((info?.urineCare || "") !== nextUrineCare) infoVariables.urineCare = nextUrineCare;
      if ((info?.suctionCare || "") !== nextSuctionCare)
        infoVariables.suctionCare = nextSuctionCare;
      if ((info?.moveCare || "") !== nextMoveCare) infoVariables.moveCare = nextMoveCare;
      if ((info?.bedCare || "") !== nextBedCare) infoVariables.bedCare = nextBedCare;
      if ((info?.introduce || "") !== nextIntroduce) infoVariables.introduce = nextIntroduce;

      if (Object.keys(infoVariables).length > 1) {
        const infoRes = await editCaregiverInfo({ variables: infoVariables });
        if (!infoRes?.data?.editCaregiverInfo?.ok) {
          throw new Error(
            infoRes?.data?.editCaregiverInfo?.error || "간병인 추가 정보 수정에 실패했습니다."
          );
        }
      }

      if (Object.keys(accountVariables).length === 1 && Object.keys(infoVariables).length === 1) {
        toast.info("변경된 내용이 없습니다.", {
          autoClose: 2500,
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      toast.success("간병인 회원 정보 수정이 완료되었습니다.", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
      history.push(`/admin/caregivers/${userCode}`);
    } catch (e) {
      toast.error(e?.message || "간병인 회원 수정 중 오류가 발생했습니다.", {
        autoClose: 3000,
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  if (!isValidUserCode) return <></>;
  if (loading) return <></>;

  const profile = data?.viewProfile;
  const info = profile?.caregiverInfo || {};

  return (
    <div className="content">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="card-heading">간병인 계정 정보 수정</div>
          <CardBody>
            <FormGroup row>
              <Label for="userId" sm={2} className="control-label">
                아이디
              </Label>
              <Col sm={10}>
                <input
                  id="userId"
                  name="userId"
                  className="form-control"
                  type="text"
                  disabled
                  defaultValue={profile?.userId}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="userName" sm={2} className="control-label">
                이름
              </Label>
              <Col sm={10}>
                <input
                  id="userName"
                  name="userName"
                  className="form-control"
                  placeholder="이름을 입력해주세요."
                  type="text"
                  defaultValue={profile?.userName}
                  ref={register({ required: "이름을 입력해주세요." })}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="phone" sm={2} className="control-label">
                연락처
              </Label>
              <Col sm={10}>
                <input
                  id="phone"
                  name="phone"
                  className="form-control"
                  placeholder="숫자만 입력 (예: 01012345678)"
                  type="text"
                  defaultValue={profile?.phone}
                  ref={register({ required: "연락처를 입력해주세요." })}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="password" sm={2} className="control-label">
                비밀번호
              </Label>
              <Col sm={10}>
                <input
                  id="password"
                  name="password"
                  className="form-control"
                  placeholder="변경 시에만 입력"
                  type="password"
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="createdAtDate" sm={2} className="control-label">
                가입일
              </Label>
              <Col sm={10}>
                <input
                  id="createdAtDate"
                  name="createdAtDate"
                  className="form-control"
                  type="date"
                  defaultValue={formatDateForInputFromMs(parseInt(profile?.createdAt, 10))}
                  ref={register()}
                />
              </Col>
            </FormGroup>
          </CardBody>
        </Card>

        <Card>
          <div className="card-heading">간병인 추가 정보 수정</div>
          <CardBody>
            <FormGroup row>
              <Label for="address" sm={2} className="control-label">
                주소
              </Label>
              <Col sm={10}>
                <input
                  id="address"
                  name="address"
                  className="form-control"
                  placeholder="주소"
                  type="text"
                  defaultValue={info?.address || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="addressDetail" sm={2} className="control-label">
                상세주소
              </Label>
              <Col sm={10}>
                <input
                  id="addressDetail"
                  name="addressDetail"
                  className="form-control"
                  placeholder="상세주소"
                  type="text"
                  defaultValue={info?.addressDetail || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="residentNumber" sm={2} className="control-label">
                주민등록번호
              </Label>
              <Col sm={10}>
                <input
                  id="residentNumber"
                  name="residentNumber"
                  className="form-control"
                  placeholder="주민등록번호"
                  type="text"
                  defaultValue={info?.residentNumber || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="smoke" sm={2} className="control-label">
                흡연 여부
              </Label>
              <Col sm={4}>
                <input
                  id="smoke"
                  name="smoke"
                  className="form-control"
                  placeholder="예: 흡연/비흡연"
                  type="text"
                  defaultValue={info?.smoke || ""}
                  ref={register()}
                />
              </Col>

              <Label for="drink" sm={2} className="control-label">
                음주 여부
              </Label>
              <Col sm={4}>
                <input
                  id="drink"
                  name="drink"
                  className="form-control"
                  placeholder="예: 음주/비음주"
                  type="text"
                  defaultValue={info?.drink || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="career" sm={2} className="control-label">
                경력(개월)
              </Label>
              <Col sm={10}>
                <input
                  id="career"
                  name="career"
                  className="form-control"
                  placeholder="예: 6 / 12 / 24"
                  type="text"
                  defaultValue={info?.career || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="mealCare" sm={2} className="control-label">
                식사케어
              </Label>
              <Col sm={4}>
                <input
                  id="mealCare"
                  name="mealCare"
                  className="form-control"
                  placeholder="가능한 식사케어"
                  type="text"
                  defaultValue={info?.mealCare || ""}
                  ref={register()}
                />
              </Col>

              <Label for="urineCare" sm={2} className="control-label">
                대소변케어
              </Label>
              <Col sm={4}>
                <input
                  id="urineCare"
                  name="urineCare"
                  className="form-control"
                  placeholder="가능한 대소변케어"
                  type="text"
                  defaultValue={info?.urineCare || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="suctionCare" sm={2} className="control-label">
                석션케어
              </Label>
              <Col sm={4}>
                <input
                  id="suctionCare"
                  name="suctionCare"
                  className="form-control"
                  placeholder="가능한 석션케어"
                  type="text"
                  defaultValue={info?.suctionCare || ""}
                  ref={register()}
                />
              </Col>

              <Label for="moveCare" sm={2} className="control-label">
                이동케어
              </Label>
              <Col sm={4}>
                <input
                  id="moveCare"
                  name="moveCare"
                  className="form-control"
                  placeholder="가능한 이동케어"
                  type="text"
                  defaultValue={info?.moveCare || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="bedCare" sm={2} className="control-label">
                침대케어
              </Label>
              <Col sm={10}>
                <input
                  id="bedCare"
                  name="bedCare"
                  className="form-control"
                  placeholder="가능한 침대케어"
                  type="text"
                  defaultValue={info?.bedCare || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>

            <FormGroup row>
              <Label for="introduce" sm={2} className="control-label">
                소개
              </Label>
              <Col sm={10}>
                <textarea
                  id="introduce"
                  name="introduce"
                  className="form-control"
                  placeholder="간단한 소개"
                  style={{ maxHeight: 160, height: 160 }}
                  defaultValue={info?.introduce || ""}
                  ref={register()}
                />
              </Col>
            </FormGroup>
          </CardBody>
        </Card>

        <Row>
          <Col xs={4} className="text-left">
            <Button
              onClick={() => history.push(`/admin/caregivers/${userCode}`)}
              className="btn-white"
              type="button"
              disabled={saving}
            >
              <i className="fa fa-list"></i>
              돌아가기
            </Button>
          </Col>
          <Col xs={4} className="text-center">
            <Button type="submit" className="btn-inverse" disabled={saving}>
              <i className="fa fa-check"></i>
              확인
            </Button>
          </Col>
          <Col xs={4} className="text-right"></Col>
        </Row>
      </form>
    </div>
  );
}

export default CaregiverEdit;

